import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import "./styles/homeBlogsSection.css";

function formatDate(dateValue) {
  if (!dateValue) return "";
  return new Date(dateValue).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

function getReadTime(content = "", excerpt = "", title = "") {
  const fullText = `${title} ${excerpt} ${content}`.trim();
  const wordCount = fullText ? fullText.split(/\s+/).filter(Boolean).length : 0;
  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return `${minutes} min read`;
}

function getBlogType(blog) {
  if (blog.type) return blog.type;
  return "Article";
}

function CardSkeleton() {
  return (
    <div className="home-blog-card is-skeleton" aria-hidden="true">
      <div className="home-blog-frame skeleton" />
      <div className="home-blog-body">
        <div className="skeleton skeleton-line skeleton-line--meta" />
        <div className="skeleton skeleton-line skeleton-line--title" />
        <div className="skeleton skeleton-line skeleton-line--title short" />
        <div className="skeleton skeleton-line skeleton-line--cta" />
      </div>
    </div>
  );
}

export default function HomeBlogsSection() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeCategory, setActiveCategory] = useState("");

  useEffect(() => {
    const fetchLatestBlogs = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const res = await api.get("/blogs/published/latest?limit=12");
        const fetchedBlogs = res.data?.blogs || [];

        setBlogs(fetchedBlogs);

        const firstCategory =
          fetchedBlogs.find((blog) => blog.category?.trim())?.category || "";

        setActiveCategory(firstCategory);
      } catch (error) {
        console.error("Latest blogs fetch error:", error);
        setErrorMessage("Unable to load blogs right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchLatestBlogs();
  }, []);

  const categories = useMemo(() => {
    return [...new Set(blogs.map((blog) => blog.category).filter(Boolean))];
  }, [blogs]);

  const filteredBlogs = useMemo(() => {
    const categoryBlogs = activeCategory
      ? blogs.filter((blog) => blog.category === activeCategory)
      : blogs;

    return categoryBlogs.slice(0, 3);
  }, [blogs, activeCategory]);

  const exploreHref = activeCategory
    ? `/blogs?category=${encodeURIComponent(activeCategory)}`
    : "/blogs";

  return (
    <section className="home-blogs-section">
      <div className="home-blogs-container">
        <aside className="home-blogs-sidebar">
          <div className="home-blogs-eyebrow">
            <span className="live-dot" aria-hidden="true" />
            Latest dispatches
          </div>

          <h2 className="home-blogs-sidebar-title">Blogs</h2>
          <p className="home-blogs-sidebar-desc">
            Field notes on data protection, compliance, and the systems
            that keep them safe.
          </p>

          {loading ? (
            <ul className="home-blogs-directory" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <li key={i}>
                  <span className="skeleton skeleton-line skeleton-line--tab" />
                </li>
              ))}
            </ul>
          ) : categories.length > 0 ? (
            <nav aria-label="Blog categories">
              <ul className="home-blogs-directory">
                {categories.map((category) => (
                  <li key={category}>
                    <button
                      type="button"
                      className={
                        activeCategory === category
                          ? "directory-item active"
                          : "directory-item"
                      }
                      aria-pressed={activeCategory === category}
                      onClick={() => setActiveCategory(category)}
                    >
                      <span className="directory-slash" aria-hidden="true">
                        /
                      </span>
                      {category}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          ) : (
            <p className="home-blogs-sidebar-empty">No categories found yet.</p>
          )}
        </aside>

        <div className="home-blogs-content">
          {loading ? (
            <div className="home-blogs-grid">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : errorMessage ? (
            <div className="home-blogs-state error">
              <span className="state-mark" aria-hidden="true">
                [ ! ]
              </span>
              <p>{errorMessage}</p>
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="home-blogs-state empty">
              <span className="state-mark" aria-hidden="true">
                [ ]
              </span>
              <p>No blogs found for this category yet.</p>
            </div>
          ) : (
            <>
              <div className="home-blogs-grid">
                {filteredBlogs.map((blog) => {
                  const blogType = getBlogType(blog);
                  const isVideo = blogType === "Video";

                  return (
                    <article key={blog.id} className="home-blog-card">
                      <Link
                        to={`/blogs/${blog.slug}`}
                        className="home-blog-frame"
                        aria-label={blog.title}
                      >
                        {blog.coverImage ? (
                          <img
                            src={blog.coverImage}
                            alt={blog.title}
                            className="home-blog-image"
                            loading="lazy"
                          />
                        ) : (
                          <div className="home-blog-image placeholder">
                            No image
                          </div>
                        )}

                        <span className="home-blog-tag">
                          {blog.category || "Services"}
                        </span>

                        <span className="scan-corner tl" aria-hidden="true" />
                        <span className="scan-corner tr" aria-hidden="true" />
                        <span className="scan-corner bl" aria-hidden="true" />
                        <span className="scan-corner br" aria-hidden="true" />
                      </Link>

                      <div className="home-blog-body">
                        <div className="home-blog-meta">
                          <span>
                            {formatDate(blog.publishedAt || blog.createdAt)}
                          </span>
                          <span className="meta-dot" aria-hidden="true">
                            &middot;
                          </span>
                          <span>{blogType}</span>
                          <span className="meta-dot" aria-hidden="true">
                            &middot;
                          </span>
                          <span>
                            {isVideo
                              ? blog.readTime
                                ? `${blog.readTime} min watch`
                                : "Watch"
                              : getReadTime(blog.content, blog.excerpt, blog.title)}
                          </span>
                        </div>

                        <h3 className="home-blog-title">
                          <Link to={`/blogs/${blog.slug}`}>{blog.title}</Link>
                        </h3>

                        <Link
                          to={`/blogs/${blog.slug}`}
                          className="home-blog-preview-link"
                        >
                          Read article <span aria-hidden="true">&rarr;</span>
                        </Link>

                        <div className="home-blog-divider" />

                        <div className="home-blog-source">
                          <span>From</span>
                          <span className="home-blog-source-mark">
                            ALG Data Guard
                          </span>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="home-blogs-cta-wrap">
                <Link to={exploreHref} className="home-blogs-cta">
                  View all {activeCategory || "blog"} articles
                  <span aria-hidden="true">&rarr;</span>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
